// errores.js — traduce un error de Supabase, PostgREST, Storage, las edge
// functions o la red a un mensaje en español para la pantalla.
//
// FUENTE DE VERDAD: shared/vls-ui/errores.js. Se copia a src/lib/ de cada app
// con scripts/sync-ui.sh (--only=errores). NO editar la copia de una app.
//
// Base: vlscr-comisiones/src/lib/errores.js (revisión de redacción
// 2026-10-08) y vls-horario/src/lib/errores.js (sesión vencida, red y UUIDs).
// Auditoría de UI 2026-10-20: casi todas las apps mostraban `e.message` crudo,
// en inglés ("Failed to fetch", "new row violates row-level security policy",
// "JWT expired", "Edge Function returned a non-2xx status code").
//
// Regla: al usuario nunca le llega un detalle técnico (códigos, nombres de
// tablas o funciones, UUIDs, texto de Postgres). El error completo va a la
// consola (una vez por error), para quien diagnostica.
//
// ─── Uso (mecánico) ──────────────────────────────────────────────────────
//   ANTES:   } catch (e) { setError(e.message); }
//   DESPUÉS: import { mensajeError } from '../lib/errores';
//            } catch (e) { setError(mensajeError(e)); }
//   Con lo que no se hizo al frente:
//            setError(mensajeError(e, { contexto: 'El pago no se guardó.' }));
//            -> "El pago no se guardó. Sin conexión. Revise su internet e intente de nuevo."
//   Compatible con la firma de comisiones: mensajeError(e, 'Texto por defecto').
//
// API:
//   mensajeError(err, opciones?) -> string
//     opciones: string (= porDefecto) u objeto
//       { porDefecto, contexto, online (navigator.onLine), registrar (true) }
//   tipoError(err, { online }?) -> 'red' | 'sesion' | 'permiso' | 'inexistente'
//       | 'concurrente' | 'tiempo' | 'servicio' | 'datos' | 'no-encontrado'
//       | 'propio' (mensaje en español escrito por nosotros) | 'otro'
//   registrarError(err, donde?) -> manda el detalle a console.error (una vez)
//
// Los mensajes que ya vienen en español desde la base (RAISE EXCEPTION de
// nuestras funciones) se respetan: los escribimos nosotros y dicen qué hacer.
// Se les quitan los UUIDs y el prefijo técnico ("mant: ", "cables: ",
// "mant_set_pin: "), y la primera letra va en mayúscula.
//   - code P0001 (RAISE EXCEPTION sin ERRCODE): siempre es nuestro, se muestra.
//   - RAISE con ERRCODE propio (22023, 42501, 23505, P0002...): se muestra si
//     el texto está en español; Postgres y PostgREST responden en inglés y esos
//     siguen traducidos ("permission denied", "duplicate key", PGRST...).
// Pasada de UI 2026-10-20: "un problema exige foto", "Agencia no encontrada" o
// "El detalle admite hasta 300 caracteres" salían como el genérico.

export const MENSAJES = {
  red: 'Sin conexión. Revise su internet e intente de nuevo.',
  sesion: 'Su sesión venció. Vuelva a entrar e intente de nuevo.',
  permiso: 'No tiene permiso para esta acción. Pídaselo a un administrador del hub.',
  inexistente: 'Falta una actualización del sistema para esta acción. Avise a soporte.',
  concurrente: 'Otra persona cambió este registro al mismo tiempo. Refresque la pantalla e intente de nuevo.',
  duplicado: 'Ese registro ya existe (puede que otra persona lo acabe de crear). Refresque la pantalla.',
  tiempo: 'La operación tardó demasiado. Revise la conexión e intente de nuevo.',
  servicio: 'El servicio no respondió bien. Intente de nuevo en unos minutos.',
  relacionado: 'No se puede completar porque hay registros relacionados. Refresque e intente de nuevo.',
  datos: 'Hay un dato con formato inválido. Revise los campos e intente de nuevo.',
  noEncontrado: 'No se encontró el registro. Refresque la pantalla.',
  archivoGrande: 'El archivo es demasiado grande.',
  muchos: 'Demasiados intentos seguidos. Espere un momento e intente de nuevo.',
  generico: 'No se pudo completar la acción. Intente de nuevo.',
};

const RE_RED = /failed to fetch|networkerror|network request failed|load failed|fetch failed|err_internet_disconnected|err_network|network error/i;
const RE_SESION = /jwt expired|invalid jwt|jwt.*(malformed|invalid)|refresh token|auth session missing|session.*(expired|missing|not found)|token.*expired/i;
const RE_PERMISO = /forbidden|unauthorized|not authorized|permission denied|insufficient_privilege|row-level security|violates row level security/i;
const RE_INEXISTENTE = /could not find the function|function .* does not exist|relation .* does not exist|could not find the .* (table|column)|column .* does not exist|schema cache/i;
const RE_CONCURRENTE = /could not serialize|serialization failure|deadlock detected|concurrent update/i;
const RE_DUPLICADO = /duplicate key|already exists|unique constraint/i;
const RE_TIEMPO = /\b(AbortError|TimeoutError)\b|timed out|timeout|signal is aborted|statement timeout|canceling statement/i;
const RE_SERVICIO = /non-2xx|edge function|functionshttperror|functionsrelayerror|functionsfetcherror|bad gateway|service unavailable|gateway time-?out/i;
const RE_RELACIONADO = /violates foreign key/i;
const RE_DATOS = /violates check constraint|violates not-null|null value in column|invalid input syntax|invalid input value|value too long|out of range/i;
const RE_NO_ENCONTRADO = /object not found|not[_ ]found|no rows|multiple \(or no\) rows|0 rows/i;
const RE_GRANDE = /payload too large|entity too large|exceeded the maximum allowed size|file size/i;
const RE_MUCHOS = /rate limit|too many requests/i;

// Códigos de Postgres (SQLSTATE) y de PostgREST que deciden solos.
const POR_CODIGO = {
  '42501': 'permiso',
  '42883': 'inexistente', // función inexistente
  '42P01': 'inexistente', // tabla o vista inexistente
  '42703': 'inexistente', // columna inexistente
  '3F000': 'inexistente', // esquema inexistente
  PGRST202: 'inexistente', // función no está en el caché de esquema
  PGRST204: 'inexistente', // columna no está en el caché de esquema
  PGRST205: 'inexistente', // tabla no está en el caché de esquema
  PGRST301: 'sesion',
  PGRST302: 'sesion',
  PGRST303: 'sesion',
  '40001': 'concurrente', // serialization_failure
  '40P01': 'concurrente', // deadlock
  '23505': 'duplicado', // unique_violation: dos personas creando lo mismo
  '23503': 'relacionado',
  '23502': 'datos',
  '23514': 'datos',
  '22P02': 'datos',
  '22001': 'datos',
  '22003': 'datos',
  '22023': 'datos', // invalid_parameter_value (en inglés; el RAISE propio en español se muestra)
  '57014': 'tiempo', // statement timeout
  PGRST116: 'noEncontrado',
};

// Clave interna -> tipo público (tipoError).
const TIPO = {
  red: 'red', sesion: 'sesion', permiso: 'permiso', inexistente: 'inexistente',
  concurrente: 'concurrente', duplicado: 'concurrente', tiempo: 'tiempo', servicio: 'servicio',
  relacionado: 'datos', datos: 'datos', noEncontrado: 'no-encontrado', archivoGrande: 'datos',
  muchos: 'servicio',
};

// Señales de texto técnico: si aparecen, el mensaje no se muestra aunque
// parezca español.
const RE_TECNICO = /\b(relation|violates|constraint|column|schema|sqlstate|pgrst\d*|postgres|supabase|jwt|json|null value|syntax|function|exception|stack|undefined|typeerror|referenceerror)\b|[{}<>]/i;
// Español escrito por nosotros: tildes, eñe o signos de apertura, o una
// palabra en español sin ninguna palabra en inglés (los RAISE se escriben sin
// tilde: "sesion de PIN vencida o de otro dia").
const RE_ES_MARCA = /[áéíóúñ¿¡]/i;
const RE_ES_PALABRA = /\b(la|el|los|las|del|de|que|para|por|un|una|unos|se|es|ya|esta|ese|esa|este|sin|con|hay|su|al|ni|solo|tiene|puede|debe|existe|registro|pago|fecha|monto|falta|exige|admite|admiten|hasta|otro|otra|dia|dias|ningun|ninguna|encontrada|encontrado|vencida|vencido|requerida|requerido|disponible|desconocida|desconocido|invalidas|invalidos|valida|valido|repetido|repetida|demasiados|demasiadas|registros|finca|actividad|transicion|invalido|invalida|inexistente|permitido|persona|sesion|tarea|area|nombre|caracteres|reporte)\b/i;
const RE_INGLES = /\b(the|of|for|to|is|are|was|not|does|did|denied|violates|invalid|value|row|rows|failed|failure|error|cannot|could|must|already|exists|returned|found|policy|permission|key|type|input|duplicate|missing|expired|unauthorized|forbidden|request|network|timeout|with|from|this|that|unexpected|unknown|should|least|allowed)\b/i;
// Prefijo técnico de nuestros RAISE: "mant: ", "cables: ", "mant_set_pin: ".
const RE_PREFIJO = /^[a-z_]+:\s+/;
const RE_UUID = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

function textoDe(err) {
  if (err == null) return '';
  if (typeof err === 'string') return err;
  const partes = [err.message, err.error_description, typeof err.error === 'string' ? err.error : err.error?.message, err.msg];
  return partes.filter((p) => typeof p === 'string' && p).join(' ');
}

function codigoDe(err) {
  if (!err || typeof err !== 'object') return '';
  const c = err.code ?? err.error?.code ?? err.sqlState;
  return c == null ? '' : String(c).toUpperCase();
}

function estadoDe(err) {
  if (!err || typeof err !== 'object') return 0;
  const s = err.status ?? err.statusCode ?? err.context?.status ?? err.error?.status;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function sinPrefijo(texto) {
  return String(texto || '').replace(RE_PREFIJO, '');
}

function esEspanolPropio(texto) {
  const t = sinPrefijo(texto);
  if (!t || RE_TECNICO.test(t)) return false;
  if (RE_ES_MARCA.test(t)) return true;
  if (RE_INGLES.test(t)) return false;
  return RE_ES_PALABRA.test(t);
}

// RAISE con prefijo nuestro ("mant: ip requerida") que llega con código de la
// base: basta con que no sea texto técnico ni inglés.
function esRaiseConPrefijo(texto) {
  if (!RE_PREFIJO.test(texto || '')) return false;
  const t = sinPrefijo(texto);
  return !!t && !RE_TECNICO.test(t) && !RE_INGLES.test(t);
}

// Texto del RAISE para la pantalla: sin UUIDs, sin prefijo, con mayúscula.
function textoPropio(err) {
  const t = sinPrefijo(textoDe(err).replace(RE_UUID, '').replace(/\s{2,}/g, ' ').trim()).trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
}

// Devuelve la clave interna de MENSAJES, 'propio' o 'generico'.
function clasificar(err, online) {
  if (online === false) return 'red';
  if (err == null || err === '') return 'generico';
  const codigo = codigoDe(err);
  const texto = textoDe(err);
  const estado = estadoDe(err);
  const nombre = typeof err === 'object' ? String(err.name || '') : '';

  // RAISE EXCEPTION sin ERRCODE: lo escribimos nosotros, se muestra tal cual.
  if (codigo === 'P0001' && textoPropio(err)) return 'propio';
  // RAISE con ERRCODE (42501 "Ya no coordina esta sección", 22023, 23505...):
  // si está en español es nuestro y es más preciso que el genérico.
  if (codigo && !codigo.startsWith('PGRST') && (esEspanolPropio(texto) || esRaiseConPrefijo(texto))) return 'propio';
  if (codigo && POR_CODIGO[codigo]) return POR_CODIGO[codigo];
  if (estado === 401 || RE_SESION.test(texto)) return 'sesion';
  if (estado === 403 || RE_PERMISO.test(texto)) return 'permiso';
  if (nombre === 'AbortError' || nombre === 'TimeoutError' || estado === 408 || estado === 524 || RE_TIEMPO.test(texto)) return 'tiempo';
  if (RE_RED.test(texto)) return 'red';
  if (RE_INEXISTENTE.test(texto)) return 'inexistente';
  if (RE_CONCURRENTE.test(texto)) return 'concurrente';
  if (RE_DUPLICADO.test(texto)) return 'duplicado';
  if (estado === 413 || RE_GRANDE.test(texto)) return 'archivoGrande';
  if (estado === 429 || RE_MUCHOS.test(texto)) return 'muchos';
  if (estado >= 500 || RE_SERVICIO.test(texto)) return 'servicio';
  if (RE_RELACIONADO.test(texto)) return 'relacionado';
  if (RE_DATOS.test(texto)) return 'datos';
  if (estado === 404 || RE_NO_ENCONTRADO.test(texto)) return 'noEncontrado';
  if (esEspanolPropio(texto)) return 'propio';
  return 'generico';
}

const registrados = typeof WeakSet === 'function' ? new WeakSet() : null;
const registradosTexto = new Set();

export function registrarError(err, donde = '') {
  if (err == null || typeof console === 'undefined') return;
  if (typeof err === 'object') {
    if (registrados?.has(err)) return;
    registrados?.add(err);
  } else {
    const clave = `${donde}|${err}`;
    if (registradosTexto.has(clave)) return;
    if (registradosTexto.size > 200) registradosTexto.clear();
    registradosTexto.add(clave);
  }
  console.error(`[vls] ${donde || 'error'}:`, err);
}

function online() {
  return typeof navigator !== 'undefined' && navigator && typeof navigator.onLine === 'boolean' ? navigator.onLine : true;
}

export function tipoError(err, { online: enLinea = online() } = {}) {
  const clave = clasificar(err, enLinea);
  if (clave === 'propio') return 'propio';
  return TIPO[clave] || 'otro';
}

export function mensajeError(err, opciones = {}) {
  const o = typeof opciones === 'string' ? { porDefecto: opciones } : (opciones || {});
  const { porDefecto = MENSAJES.generico, contexto = '', registrar = true } = o;
  const enLinea = 'online' in o ? o.online : online();
  if (registrar && err != null && err !== '') registrarError(err, contexto);
  const clave = clasificar(err, enLinea);
  let frase;
  if (clave === 'propio') frase = textoPropio(err);
  else if (clave === 'generico') frase = porDefecto;
  else frase = MENSAJES[clave];
  return contexto ? `${contexto.trim()} ${frase}` : frase;
}

export default mensajeError;
